import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'react-feather';
import PageSeo from './components/PageSeo';
import { useDarkMode } from './DarkModeContext';
import { trackEvent } from './utils/amplitude';
import Footer from './Footer';

function Career() {
    const { darkMode } = useDarkMode();

    useEffect(() => {
        trackEvent('page_viewed', { pageName: 'Career' });
    }, []);
    
    return (
        <>
        <PageSeo
            title="Careers at Merra AI – Join Our Team"
            description="Join Merra AI. We hire top coders, marketers, and salespeople who build at extreme speed with extreme ambition."
            path="/career"
            keywords="Merra careers, jobs at Merra, work at Merra, Merra hiring, tech jobs"
            ogDescription="Join Merra's team of top 1% coders, marketers, and salespeople."
        />
        <div style={{ 
            maxWidth: '800px', 
            margin: '0 auto', 
            padding: '20px',
            fontFamily: 'Arial, sans-serif',
            lineHeight: '1.6',
            color: darkMode ? '#e0e0e0' : '#333',
            backgroundColor: darkMode ? '#1a1a1a' : 'white',
            minHeight: '100vh'
        }}>
            <div style={{ marginBottom: '30px' }}>
                <Link to="/" style={{ color: darkMode ? '#4A9EFF' : '#1976d2', textDecoration: 'none' }}>
                    ← Back to Home
                </Link>
            </div>

            <h1 style={{ color: darkMode ? '#ffffff' : '#2c3e50', borderBottom: `2px solid ${darkMode ? '#AB5898' : '#AB5898'}`, paddingBottom: '10px' }}>
                Careers at Merra
            </h1>

            <div style={{ marginTop: '30px', marginBottom: '50px' }}>
                <p style={{ marginBottom: '30px', fontSize: '20px', fontWeight: '500', color: darkMode ? '#ffffff' : '#2c3e50' }}>
                    We hire top 1% coders, top 1% marketing team, and top 1% salespeople.
                </p>
                <p style={{ marginBottom: '30px', fontSize: '20px', fontWeight: '500', color: darkMode ? '#ffffff' : '#2c3e50' }}>
                    We build at extreme speed.
                </p>
                <p style={{ marginBottom: '40px', fontSize: '20px', fontWeight: '500', color: darkMode ? '#ffffff' : '#2c3e50' }}>
                    We hire people who move even faster.
                </p>
                <p style={{ marginBottom: '40px', fontSize: '20px', fontWeight: '500', color: darkMode ? '#ffffff' : '#2c3e50' }}>
                    With extreme ambition.
                </p>
                
                <p style={{ marginBottom: '20px', fontSize: '18px', lineHeight: '1.8', color: darkMode ? '#e0e0e0' : '#333' }}>
                    Not your average startup.
                </p>
                <p style={{ marginBottom: '20px', fontSize: '18px', lineHeight: '1.8', color: darkMode ? '#e0e0e0' : '#333' }}>
                    This is not a place for rebels.
                </p>
                <p style={{ marginBottom: '20px', fontSize: '18px', lineHeight: '1.8', color: darkMode ? '#e0e0e0' : '#333' }}>
                    Rebels fight the system.
                </p>
                <p style={{ marginBottom: '40px', fontSize: '18px', lineHeight: '1.8', color: darkMode ? '#e0e0e0' : '#333' }}>
                    Builders design the new one.
                </p>
                
                <p style={{ marginBottom: '40px', fontSize: '18px', lineHeight: '1.8', color: darkMode ? '#e0e0e0' : '#333' }}>
                    We look for people who are relentlessly driven, mission aligned, selfless, and obsessed with shaping the future of storytelling.
                </p>

                <p style={{ marginBottom: '40px', fontSize: '18px', lineHeight: '1.8', color: darkMode ? '#e0e0e0' : '#333' }}>
                    We look for people who lean into the challenge, embrace it, chase it.
                </p>

                <p style={{ marginBottom: '40px', fontSize: '18px', lineHeight: '1.8', color: darkMode ? '#e0e0e0' : '#333' }}>
                    We want to do something that has never been done before, so expect to do something that has never been done before.
                </p>
                
                <p style={{ marginBottom: '40px', fontSize: '18px', lineHeight: '1.8', color: darkMode ? '#e0e0e0' : '#333' }}>
                    We are building the creative engine for the next generation of founders, creators, and businesses. As technology empowers more people to build companies and grow brands, the future will be shaped by millions of new entrepreneurs and creators, each with a message to share and a story to tell. Merra exists to give them the speed, clarity, and momentum to tell it.
                </p>
                
                <h2 style={{ 
                    color: darkMode ? '#ffffff' : '#2c3e50', 
                    marginTop: '50px', 
                    marginBottom: '30px',
                    fontSize: '24px',
                    fontWeight: '600'
                }}>
                    Momentum Philosophy
                </h2>
                
                <p style={{ marginBottom: '20px', fontSize: '18px', lineHeight: '1.8', color: darkMode ? '#e0e0e0' : '#333' }}>
                    We operate on momentum: small steps done immediately beat big plans delayed indefinitely. Speed is not a tactic here, it's a culture. So we eliminate friction everywhere. We don't wait for perfect conditions, we create motion. Our biases are action and speed. Because consistency compounds faster than brilliance.
                </p>
                
                <p style={{ marginBottom: '20px', fontSize: '18px', lineHeight: '1.8', color: darkMode ? '#e0e0e0' : '#333', fontStyle: 'italic' }}>
                    The daily question: figure out what we should accomplish in 30 days, and find out how to do that in 1 day.
                </p>
                
                <p style={{ marginBottom: '40px', fontSize: '18px', lineHeight: '1.8', color: darkMode ? '#e0e0e0' : '#333' }}>
                    Everything we do is in intensity. We ship daily, fix daily, post daily, distribute daily, talk to users daily (and balance it with 1 extreme sport activity daily).
                </p>
                
                <h2 style={{ 
                    color: darkMode ? '#ffffff' : '#2c3e50', 
                    marginTop: '50px', 
                    marginBottom: '30px',
                    fontSize: '24px',
                    fontWeight: '600'
                }}>
                    Values
                </h2>
                
                <ul style={{ 
                    listStyle: 'none', 
                    padding: 0, 
                    marginBottom: '40px',
                    fontSize: '18px',
                    lineHeight: '1.8',
                    color: darkMode ? '#e0e0e0' : '#333'
                }}>
                    <li style={{ marginBottom: '15px', paddingLeft: '20px', position: 'relative' }}>
                        <span style={{ position: 'absolute', left: 0 }}>•</span>
                        exceptional minds (intelligence and mental toughness)
                    </li>
                    <li style={{ marginBottom: '15px', paddingLeft: '20px', position: 'relative' }}>
                        <span style={{ position: 'absolute', left: 0 }}>•</span>
                        extreme ambition
                    </li>
                    <li style={{ marginBottom: '15px', paddingLeft: '20px', position: 'relative' }}>
                        <span style={{ position: 'absolute', left: 0 }}>•</span>
                        courage in imperfection
                    </li>
                    <li style={{ marginBottom: '15px', paddingLeft: '20px', position: 'relative' }}>
                        <span style={{ position: 'absolute', left: 0 }}>•</span>
                        perfection is procrastination
                    </li>
                    <li style={{ marginBottom: '15px', paddingLeft: '20px', position: 'relative' }}>
                        <span style={{ position: 'absolute', left: 0 }}>•</span>
                        time is sacred
                    </li>
                    <li style={{ marginBottom: '15px', paddingLeft: '20px', position: 'relative' }}>
                        <span style={{ position: 'absolute', left: 0 }}>•</span>
                        innovate / first movers
                    </li>
                    <li style={{ marginBottom: '15px', paddingLeft: '20px', position: 'relative' }}>
                        <span style={{ position: 'absolute', left: 0 }}>•</span>
                        relentless expression
                    </li>
                </ul>
                
                <p style={{ fontSize: '18px', lineHeight: '1.8', color: darkMode ? '#e0e0e0' : '#333' }}>
                    If that speaks to you, reach out to{' '}
                    <a 
                        href="https://www.linkedin.com/in/boukar-sall" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        style={{
                            color: darkMode ? '#4A9EFF' : '#1976d2', 
                            textDecoration: 'none', 
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            gap: '4px'
                        }}
                    >
                        Boukar <ExternalLink size={14} />
                    </a>
                </p>
            </div>
            <Footer />
        </div>
        </>
    );
}

export default Career;

